import React, { useState, useEffect, useRef } from 'react';
import { api } from '@/api/apiClient';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useEnrollmentCart } from '@/components/enrollment/EnrollmentCartContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PenLine, Loader2, CheckCircle2, AlertCircle, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { downloadPDF } from '@/components/utils/pdfGeneration';
import { sendEnrollmentConfirmation } from '@/components/email/sendEmail';

export default function EnrollmentSign() {
  const { cartItems, clearCart } = useEnrollmentCart();
  const [employee, setEmployee] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const persistStep = async () => {
      if (employee?.id) {
        try {
          await api.entities.Employee.update(employee.id, { last_enrollment_step: 'sign' });
        } catch (err) {
          console.error('Failed to persist enrollment sign step', err);
        }
      }
    };
    persistStep();
  }, [employee?.id]);

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate(createPageUrl('BenefitsCatalog'));
    }
  }, [cartItems]);

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      
      if (employees.length > 0) {
        setEmployee(employees[0]);
        const employers = await api.entities.Employer.filter({ id: employees[0].employer_id });
        if (employers.length > 0) setEmployer(employers[0]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      setSignature(canvasRef.current.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature('');
  };

  const handleSubmit = async () => {
    if (!employee || !signature || !agreedToTerms) return;
    
    setSubmitting(true);
    try {
      const createdEnrollments = [];
      
      // Create enrollment for each cart item
      for (const item of cartItems) {
        const enrollmentData = {
          employee_id: employee.id,
          employer_id: employee.employer_id,
          product_id: item.product_id,
          coverage_level: item.coverage_level,
          status: 'active',
          monthly_premium: item.monthly_premium,
          employer_contribution: 0, // No employer contribution for now
          employee_cost: item.monthly_premium, // Employee pays the full premium
          coverage_amount: item.coverage_amount || 0,
          effective_date: new Date().toISOString(), // ISO-8601 DateTime format
          signed_at: new Date().toISOString(),
          signature_data: signature
        };
        
        const enrollment = await api.entities.Enrollment.create(enrollmentData);
        createdEnrollments.push(enrollment);

        // Log each enrollment
        await api.entities.AuditLog.create({
          entity_type: 'Enrollment',
          entity_id: enrollment.id,
          action: 'enroll',
          performed_by: employee.user_email,
          notes: `Enrolled in ${item.product_name} - ${item.coverage_level}`
        });
      }

      // Update employee status
      await api.entities.Employee.update(employee.id, {
        enrollment_status: 'signed',
        has_seen_welcome: true,
        last_enrollment_step: 'done'
      });

      // Generate PDFs and send confirmation email
      try {
        const products = await api.entities.BenefitProduct.list();
        const familyMembers = await api.entities.FamilyMember.filter({ 
          employee_id: employee.id,
          is_active: true
        });

        // Generate single bulk enrollment PDF with all benefits
        const { generateBulkEnrollmentPDF } = await import('@/components/utils/pdfGeneration');
        const pdf = await generateBulkEnrollmentPDF(
          createdEnrollments, 
          employee, 
          employer, 
          products, 
          familyMembers, 
          signature
        );
        
        // Save PDF as data URL and update enrollments
        const pdfDataUrl = pdf.output('dataurlstring');
        downloadPDF(pdf, `benefits_enrollment_${employee.last_name}_${format(new Date(), 'yyyyMMdd')}.pdf`);
        
        // Update each enrollment with the PDF URL
        for (const enrollment of createdEnrollments) {
          await api.entities.Enrollment.update(enrollment.id, {
            enrollment_pdf_url: pdfDataUrl
          });
        }

        // Send single confirmation email for all benefits
        const totalPremium = cartItems.reduce((sum, item) => sum + item.monthly_premium, 0);
        const benefitsList = cartItems.map(item => `${item.product_name} (${item.coverage_level})`).join(', ');
        
        const emailData = sendEnrollmentConfirmation(employee.user_email, {
          employeeName: `${employee.first_name} ${employee.last_name}`,
          productName: `${cartItems.length} Benefits Package`,
          coverageLevel: benefitsList,
          monthlyPremium: totalPremium.toFixed(2),
          coverageAmount: 'Multiple',
          effectiveDate: format(new Date(), 'MMMM d, yyyy'),
          taxSavings: '0',
          dashboardUrl: globalThis.location.origin + '/EmployeeDashboard',
          employerName: employer?.company_name || 'Your Benefits Team'
        });

        await api.integrations.Core.SendEmail({
          to: emailData.to,
          subject: emailData.subject,
          body: emailData.html
        });
      } catch (error_) {
        console.error('PDF/Email error:', error_);
      }

      clearCart();
      toast.success('Enrollment completed successfully!');
      navigate(createPageUrl('EmployeeDashboard'));
    } catch (err) {
      console.error(err);
      toast.error('Failed to complete enrollment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-6">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Back Button */}
        <Link to={createPageUrl('EnrollmentReview')}>
          <Button variant="ghost" className="text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Review
          </Button>
        </Link>

        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Sign Your Enrollment</h1>
          <p className="text-slate-600 mt-2">Final step - review and sign to complete your enrollment</p>
        </div>

        {/* Enrolled Benefits Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Benefits Being Enrolled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-semibold text-slate-900">{item.product_name}</p>
                    <p className="text-sm text-slate-500">{item.coverage_level.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' + ')}</p>
                  </div>
                  <p className="text-lg font-bold text-emerald-600">${item.monthly_premium.toFixed(2)}/mo</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Terms and Conditions */}
        <Card>
          <CardHeader>
            <CardTitle>Enrollment Agreement - Please Read Carefully</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="h-80 overflow-y-auto p-4 bg-slate-50 rounded-lg text-sm text-slate-600 space-y-4">
              <h4 className="font-semibold text-slate-800 text-base">BENEFIT ENROLLMENT AGREEMENT</h4>
              <p className="font-medium text-slate-700">By signing below, I acknowledge and agree to the following terms and conditions:</p>
              
              <p><strong>1. Accuracy of Information:</strong> I certify that all information provided in this enrollment is true, complete, and accurate to the best of my knowledge. I understand that any false or misleading information may result in denial of claims, termination of coverage, or other appropriate action.</p>
              
              <p><strong>2. Premium Deductions:</strong> I authorize the deduction of applicable premiums from my paycheck as specified in this enrollment. Premiums will be deducted on a pre-tax basis under Section 125 of the Internal Revenue Code (Cafeteria Plan).</p>
              
              <p><strong>3. Tax Implications:</strong> I understand that pre-tax deductions reduce my taxable income for federal, state, and Social Security taxes. I acknowledge that I have been advised to consult with a tax professional regarding the tax implications of my benefit elections.</p>
              
              <p><strong>4. Coverage Effective Date:</strong> I understand that coverage will become effective on the date specified by my employer, subject to any waiting periods outlined in the plan documents. Coverage is subject to approval by the insurance carrier.</p>
              
              <p><strong>5. Changes and Cancellations:</strong> I understand that changes to my coverage may only be made during the annual open enrollment period or within 30 days following a qualifying life event (such as marriage, birth, adoption, divorce, or loss of other coverage) as defined by IRS regulations and plan rules.</p>
              
              <p><strong>6. Binding Elections:</strong> I acknowledge that my benefit elections are binding for the plan year and cannot be changed except as described above. I understand that I cannot drop coverage mid-year without a qualifying life event.</p>
              
              <p><strong>7. Section 125 Plan:</strong> I understand that these benefits are offered under a Section 125 Cafeteria Plan, allowing me to pay premiums with pre-tax dollars, thereby reducing my taxable income and resulting in potential tax savings.</p>
              
              <p><strong>8. Plan Documents:</strong> I acknowledge that I have access to and have reviewed the Summary Plan Descriptions (SPDs), benefit plan documents, and any other relevant materials. I understand that the official plan documents govern my coverage and benefits.</p>
              
              <p><strong>9. Family Member Verification:</strong> If I have enrolled family members, I understand that I may be required to provide documentation to verify their eligibility (such as marriage certificates, birth certificates, or adoption papers).</p>

              <p><strong>10. COBRA Rights:</strong> I understand my rights to continue coverage under COBRA (Consolidated Omnibus Budget Reconciliation Act) upon termination of employment or other qualifying events.</p>

              <p><strong>11. Privacy and HIPAA:</strong> I consent to the collection, use, and disclosure of my personal health information as necessary to administer my benefits, in accordance with HIPAA (Health Insurance Portability and Accountability Act) regulations.</p>

              <p><strong>12. Electronic Communications:</strong> I agree to receive benefit-related communications, including but not limited to enrollment confirmations, coverage documents, and notifications, electronically via email or through the benefits portal.</p>

              <p><strong>13. Right to Audit:</strong> I understand that the employer and/or insurance carrier reserve the right to audit my enrollment and may request supporting documentation at any time.</p>

              {cartItems.some(item => item.requires_health_questions) && (
                <Alert className="border-amber-300 bg-amber-50">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    <strong>Important:</strong> Some selected benefits may require health questionnaires or evidence of insurability. You may be contacted by the insurance carrier to complete additional forms. Coverage for these benefits is subject to approval.
                  </AlertDescription>
                </Alert>
              )}

              <div className="mt-6 pt-4 border-t border-slate-300">
                <p className="text-xs text-slate-500 italic">
                  This is a legally binding electronic agreement. Your digital signature below constitutes your acknowledgment and acceptance of these terms.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
              <Checkbox 
                id="terms" 
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                className="mt-1"
              />
              <Label htmlFor="terms" className="text-slate-700 cursor-pointer leading-relaxed">
                <span className="font-semibold">I have read and agree to the terms and conditions above.</span> I understand that this is a legally binding agreement and authorizes payroll deductions for the selected benefits. I acknowledge that I have had the opportunity to review all plan documents and ask questions about my coverage.
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Signature - Only show after agreement is checked */}
        {agreedToTerms && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PenLine className="w-5 h-5 text-emerald-500" />
                Digital Signature
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600 text-sm">Please sign below to complete your enrollment.</p>
              
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-2 bg-white">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="w-full touch-none cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            
            <div className="flex justify-between items-center">
              <p className="text-xs text-slate-500">
                Signed by: {employee?.first_name} {employee?.last_name}
              </p>
              <Button variant="outline" size="sm" onClick={clearSignature}>
                <Trash2 className="w-4 h-4 mr-2" />
                Clear
              </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit - Only show after agreement is checked */}
        {agreedToTerms && (
          <div className="flex justify-center">
          <Button 
            onClick={handleSubmit} 
            disabled={!signature || !agreedToTerms || submitting}
            className="h-16 px-12 text-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                Processing Enrollment...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 mr-3" />
                Complete Enrollment
              </>
            )}
          </Button>
        </div>
        )}
      </div>
    </div>
  );
}